// The blocking onboarding wizard. Owns the step machine: welcome → hub → a per-source
// branch → done. The step machine drives the shared `.wiz` rail (overhaul §6.2): four
// slots — Signed in ✓ → Connect a source → Your team → Start capturing — with the whole
// source/install/consent sub-flow collapsing under slot 2. Sign out is the only escape
// from the hard-lock (the App auth gate drops back to AuthScreen on SIGNED_OUT).
// `onComplete` persists first-run completion (see OnboardingGate) and releases into the
// dashboard.

import { useState, useCallback } from 'react';
import { supabase } from '../../lib/auth';
import type { ChatSourceId } from '../../lib/connect-source';
import type { WizardStep } from './wizard-step';
import { firstStepForSource, deriveBranchKind } from './wizard-step';
import { Wizard } from '../shared/Wizard';
import { WelcomeStep } from './WelcomeStep';
import { SourceHub } from './SourceHub';
import { DoneStep } from './DoneStep';
import { ChatInstallStep } from './ChatInstallStep';
import { ChatConsentStep } from './ChatConsentStep';
import { GoogleConnectStep } from './GoogleConnectStep';
import { ClaudeCodeStep } from './ClaudeCodeStep';
import { GitHubConnectStep } from './GitHubConnectStep';
import { GranolaConnectStep } from './GranolaConnectStep';
import { TeamSourceConnectStep } from './TeamSourceConnectStep';
import { TeamStep } from './TeamStep';

// Rail copy is verbatim from the mock (§6.2). Sublabels included.
const RAIL = [
  { id: 'signedin', title: 'Signed in', sub: 'with Google' },
  { id: 'connect', title: 'Connect a source', sub: 'One is enough' },
  { id: 'team', title: 'Your team', sub: 'Invite or join' },
  { id: 'capturing', title: 'Start capturing', sub: 'First briefing' },
] as const;

// Map the step machine onto the 4 rail slots: welcome → 1, the connect sub-flow → 2,
// (team → 3 wired in Task 3), the -done screens → 4.
function railIndexForStep(step: WizardStep): number {
  if (step === 'welcome') return 0;
  if (step === 'team') return 2;
  if (step.endsWith('-done')) return 3;
  return 1;
}

// After connecting, route to the Done screen. DoneStep derives its copy from sourceId, but
// keeping the branch-specific -done step makes the rail/flow explicit.
function doneStepForSource(sourceId: string | null): WizardStep {
  switch (sourceId ? deriveBranchKind(sourceId) : 'chat') {
    case 'google': return 'google-done';
    case 'claudecode': return 'claudecode-done';
    case 'github': return 'github-done';
    case 'slack': return 'slack-done';
    case 'notion': return 'notion-done';
    case 'granola': return 'granola-done';
    default: return 'chat-done';
  }
}

export function OnboardingWizard({ onComplete }: { onComplete: () => void | Promise<void> }) {
  const [step, setStep] = useState<WizardStep>('welcome');
  const [sourceId, setSourceId] = useState<string | null>(null);

  const signOut = useCallback(() => { void supabase.auth.signOut(); }, []);

  const pickSource = useCallback((id: string) => {
    setSourceId(id);
    setStep(firstStepForSource(id));
  }, []);

  const body = (() => {
    switch (step) {
      case 'welcome':
        return <WelcomeStep onNext={() => setStep('hub')} />;
      case 'hub':
        return <SourceHub onPick={pickSource} onBack={() => setStep('welcome')} onSignOut={signOut} />;
      // Chat branch: install (auto-advances on the extension handshake) → consent → done.
      case 'chat-install':
        return (
          <ChatInstallStep
            sourceId={sourceId ?? ''}
            onConnected={() => setStep('chat-consent')}
            onBack={() => setStep('hub')}
            onSignOut={signOut}
          />
        );
      case 'chat-consent':
        return (
          <ChatConsentStep
            sourceId={(sourceId ?? 'chatgpt') as ChatSourceId}
            onConnected={() => setStep('team')}
            onCancel={() => setStep('hub')}
          />
        );
      // Google branch: OAuth popup → team (readiness poll confirms the connection).
      case 'google':
        return <GoogleConnectStep onConnected={() => setStep('team')} onBack={() => setStep('hub')} />;
      // Claude Code branch: CLI setup → team (readiness poll confirms the minted key).
      case 'claudecode':
        return <ClaudeCodeStep onConnected={() => setStep('team')} onBack={() => setStep('hub')} />;
      // GitHub branch: OAuth → team (readiness poll confirms the github integration).
      case 'github':
        return <GitHubConnectStep onConnected={() => setStep('team')} onBack={() => setStep('hub')} />;
      // Granola branch: paste API key → team (connect is synchronous, advances on 200).
      case 'granola':
        return <GranolaConnectStep onConnected={() => setStep('team')} onBack={() => setStep('hub')} />;
      // Slack / Notion branches: team-workspace OAuth (a team is created on the fly if needed).
      case 'slack':
        return <TeamSourceConnectStep provider="slack" onConnected={() => setStep('team')} onBack={() => setStep('hub')} />;
      case 'notion':
        return <TeamSourceConnectStep provider="notion" onConnected={() => setStep('team')} onBack={() => setStep('hub')} />;
      // Your team — self-vs-invite fork; skippable (team is not required to capture).
      case 'team':
        return <TeamStep onContinue={() => setStep(doneStepForSource(sourceId))} onBack={() => setStep('hub')} />;
      // All -done steps terminate at the Done screen (released via onComplete).
      default:
        return <DoneStep sourceId={sourceId} onEnter={onComplete} />;
    }
  })();

  const railIndex = railIndexForStep(step);
  const steps = RAIL.map((r, i) => ({ ...r, panel: i === railIndex ? body : null }));

  return <Wizard brand="SET UP TERUM" steps={steps} current={railIndex} />;
}
